import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Upload, FileText, AlertCircle, CloudUpload, BookPlus, CheckCircle, Image } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ThemeSelector } from '@/components/ThemeSelector'
import { api } from '@/lib/api'
import { formatFileSize, buildAuthorizedUrl } from '@/lib/utils'
import { ApiError, BookDetail } from '@/types'

const bookSchema = z.object({
  title: z.string().min(1, '书名不能为空'),
  author: z.string().optional(),
  isbn: z.string().optional(),
  summary: z.string().optional(),
  tags: z.string().optional(),
  themes: z.array(z.string()).optional(),
  requiredLevel: z.coerce.number().int().min(0, '访问等级必须是非负整数').default(100),
})

type BookFormData = z.infer<typeof bookSchema>

interface UploadFile {
  file: File
  format: 'epub' | 'pdf'
}

export function UploadPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [bookId, setBookId] = useState<string>('')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})
  const [error, setError] = useState('')
  const [selectedThemes, setSelectedThemes] = useState<string[]>([])
  const [bookInfo, setBookInfo] = useState<BookDetail | null>(null)
  const [coverUploading, setCoverUploading] = useState(false)
  const [coverError, setCoverError] = useState('')
  const coverInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BookFormData>({
    resolver: zodResolver(bookSchema),
    defaultValues: {
      themes: [],
      requiredLevel: 100,
    },
  })

  useEffect(() => {
    if (!bookId) return
    loadBookInfo(bookId)
  }, [bookId])

  const loadBookInfo = async (id: string) => {
    try {
      const info = await api.getBook(id)
      setBookInfo(info)
    } catch (err) {
      console.error('加载书籍信息失败:', err)
    }
  }

  const processFiles = (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) {
      setFiles([])
      return
    }

    const validTypes: Record<UploadFile['format'], string> = {
      epub: 'application/epub+zip',
      pdf: 'application/pdf',
    }

    const validFiles: UploadFile[] = []
    const errors: string[] = []

    selectedFiles.forEach((file) => {
      const lowerName = file.name.toLowerCase()
      let format: UploadFile['format'] | null = null

      if (lowerName.endsWith('.epub')) {
        format = 'epub'
      } else if (lowerName.endsWith('.pdf')) {
        format = 'pdf'
      }

      if (!format) {
        errors.push(`文件 ${file.name} 的格式不支持（仅限 EPUB / PDF）`)
        return
      }

      const expectedType = validTypes[format]
      if (file.type && file.type !== expectedType) {
        errors.push(`文件 ${file.name} 的类型不正确，应为 ${expectedType}`)
        return
      }

      if (file.size > 200 * 1024 * 1024) {
        errors.push(`文件 ${file.name} 超过 200MB 限制`)
        return
      }

      validFiles.push({ file, format })
    })

    if (errors.length > 0) {
      setError(errors[0])
    } else {
      setError('')
    }

    setFiles(validFiles)
  }

  // Step 1: Create book record
  const onSubmit = async (data: BookFormData) => {
    setError('')
    
    try {
      const tags = data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : []

      const result = await api.createBook({
        ...data,
        tags,
        themes: selectedThemes,
        requiredLevel: data.requiredLevel ?? 100,
      })
      
      setBookId(result.id)
      setStep(2)
      await loadBookInfo(result.id)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || '创建书籍失败')
    }
  }

  // Step 2: File selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(Array.from(event.target.files || []))
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  // Step 3: Upload files
  const handleUpload = async () => {
    if (files.length === 0) {
      setError('请选择至少一个文件')
      return
    }
    
    setUploading(true)
    setError('')
    
    try {
      const processedFormats = new Set<UploadFile['format']>()

      for (let i = 0; i < files.length; i++) {
      const { file, format } = files[i]
      const fileKey = `${i}-${file.name}`

      setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }))

        // Initialize upload
        const initResult = await api.initUpload(bookId, {
          format,
          filename: file.name,
          contentType: file.type,
        })

        setUploadProgress(prev => ({ ...prev, [fileKey]: 25 }))

        // Upload file
        await api.uploadFile(bookId, file, format, initResult.r2Key)

        setUploadProgress(prev => ({ ...prev, [fileKey]: 75 }))

        // Register asset
        const isPrimaryForFormat = !processedFormats.has(format)
        await api.registerAsset(bookId, {
          format,
          r2Key: initResult.r2Key,
          filename: file.name,
          sizeBytes: file.size,
          contentType: file.type,
          isPrimary: isPrimaryForFormat,
        })

      processedFormats.add(format)
      setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }))
    }

      await loadBookInfo(bookId)

      setFiles([])
      setUploadProgress({})
      setStep(3)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || '上传失败')
    } finally {
      setUploading(false)
    }
  }

  const handleCoverUpload = async (file: File) => {
    setCoverError('')

    if (!bookId) {
      setCoverError('缺少书籍 ID，无法上传封面')
      return
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setCoverError('只支持 JPG、PNG、WebP 格式的图片')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setCoverError('图片文件不能超过 5MB')
      return
    }

    setCoverUploading(true)
    try {
      const response = await api.uploadCover(bookId, file)
      setBookInfo((prev) => (prev ? { ...prev, coverUrl: response.coverUrl } : prev))
      await loadBookInfo(bookId)
      setCoverError('')
    } catch (err) {
      const apiError = err as ApiError
      setCoverError(apiError?.message || '封面上传失败')
      console.error('封面上传失败:', err)
    } finally {
      setCoverUploading(false)
      if (coverInputRef.current) {
        coverInputRef.current.value = ''
      }
    }
  }

  const handleCoverInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    handleCoverUpload(file)
  }

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'copy'
  }

  const handleDropFiles = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const droppedFiles = Array.from(event.dataTransfer.files || [])
    processFiles(droppedFiles)
  }

  const bookInfoCoverUrl = buildAuthorizedUrl(bookInfo?.coverUrl) ?? bookInfo?.coverUrl ?? undefined

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <CloudUpload className="h-4 w-4" /> 上传中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">上传新书籍</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              按照步骤创建新书籍，上传文件并设置封面
            </p>
          </div>
        </div>
      </div>

    <div className="max-w-3xl mx-auto space-y-6">
      {/* Progress indicator */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between">
          <div className={`flex-1 flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg ${
              step >= 1 ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
            </div>
            <span className="ml-3 font-semibold">创建书籍</span>
          </div>

          <div className={`w-16 h-1 rounded-full ${step >= 2 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gray-200'}`} />

          <div className={`flex-1 flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg ${
              step >= 2 ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
            </div>
            <span className="ml-3 font-semibold">上传文件</span>
          </div>

          <div className={`w-16 h-1 rounded-full ${step >= 3 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gray-200'}`} />

          <div className={`flex-1 flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg ${
              step >= 3 ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 3 ? <CheckCircle className="h-5 w-5" /> : '3'}
            </div>
            <span className="ml-3 font-semibold">设置封面</span>
          </div>

          <div className={`w-16 h-1 rounded-full ${step >= 4 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gray-200'}`} />

          <div className={`flex-1 flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg ${
              step >= 4 ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step === 4 ? <CheckCircle className="h-5 w-5" /> : '4'}
            </div>
            <span className="ml-3 font-semibold">预览确认</span>
          </div>
        </div>
      </div>

      {/* Step 1: Create book */}
      {step === 1 && (
        <Card className="border-gray-100 shadow-xl">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <BookPlus className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="text-lg font-bold">创建新书籍</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="书名 *"
                {...register('title')}
                error={errors.title?.message}
                placeholder="输入书籍标题"
              />
              
              <Input
                label="作者"
                {...register('author')}
                error={errors.author?.message}
                placeholder="输入作者姓名"
              />
              
              <Input
                label="ISBN"
                {...register('isbn')}
                error={errors.isbn?.message}
                placeholder="978-x-xxx-xxxxx-x"
              />

              <Input
                label="访问等级 *"
                type="number"
                min={0}
                {...register('requiredLevel', { valueAsNumber: true })}
                error={errors.requiredLevel?.message}
                helper="必填：数字越小权限越高，默认 100 表示普通用户可访问"
                required
              />
              
              <Input
                label="标签"
                {...register('tags')}
                error={errors.tags?.message}
                placeholder="用逗号分隔，如：科幻,小说,经典"
              />

              <ThemeSelector
                value={selectedThemes}
                onChange={(themes) => {
                  setSelectedThemes(themes)
                  setValue('themes', themes)
                }}
              />

              <Textarea
                label="简介"
                {...register('summary')}
                error={errors.summary?.message}
                placeholder="输入书籍简介..."
                rows={4}
              />

              {error && (
                <div className="rounded-md bg-red-50 p-3 flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/')}
                >
                  取消
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                >
                  下一步
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload files */}
      {step === 2 && (
        <Card className="border-gray-100 shadow-xl">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-100 p-2">
                <CloudUpload className="h-5 w-5 text-purple-600" />
              </div>
              <CardTitle className="text-lg font-bold">上传文件</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-8">
            <div
              className="border-2 border-dashed border-purple-200 rounded-xl p-8 text-center hover:border-purple-400 bg-gradient-to-br from-purple-50/50 to-pink-50/50 transition-all"
              onDragOver={handleDragOver}
              onDrop={handleDropFiles}
            >
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label htmlFor="file-upload" className="cursor-pointer" onDragOver={handleDragOver} onDrop={handleDropFiles}>
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    点击选择文件或拖拽到此处
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    支持 EPUB 和 PDF 格式，单文件最大 200MB
                  </span>
                </label>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  multiple
                  accept=".epub,.pdf,application/epub+zip,application/pdf"
                  onChange={handleFileSelect}
                />
              </div>
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">已选择文件:</h3>
                {files.map((fileItem, index) => {
                  const fileKey = `${index}-${fileItem.file.name}`
                  const progress = uploadProgress[fileKey] || 0
                  
                  return (
                    <div key={fileKey} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium">{fileItem.file.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(fileItem.file.size)} • {fileItem.format.toUpperCase()}
                          </p>
                        </div>
                      </div>
                      
                      {uploading && progress > 0 ? (
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                            <div 
                              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{progress}%</span>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                        >
                          移除
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setStep(1)}
                disabled={uploading}
              >
                上一步
              </Button>
              <Button
                onClick={handleUpload}
                loading={uploading}
                disabled={files.length === 0}
              >
                开始上传
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Cover setup */}
      {step === 3 && (
        <Card className="border-gray-100 shadow-xl">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-emerald-100 p-2">
                <Image className="h-5 w-5 text-emerald-600" />
              </div>
              <CardTitle className="text-lg font-bold">设置封面</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center">
              <div className="mb-4">
                <div className="w-40 aspect-[2/3] overflow-hidden rounded-lg border bg-gray-100 flex items-center justify-center">
                  {bookInfoCoverUrl ? (
                    <img src={bookInfoCoverUrl} alt="书籍封面" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-sm text-gray-400">尚未上传封面</span>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500 text-center space-y-1 mb-4">
                <p>JPG、PNG、WebP</p>
                <p>建议 300×450 像素</p>
                <p>最大 5MB</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                >
                  {coverUploading ? '正在上传...' : '上传封面图片'}
                </Button>
                <input
                  ref={coverInputRef}
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleCoverInputChange}
                  disabled={coverUploading}
                />
              </div>
              {coverError && (
                <p className="mt-3 text-sm text-red-600">{coverError}</p>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep(2)} disabled={coverUploading}>
                上一步
              </Button>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setStep(4)} disabled={coverUploading}>
                  暂不设置
                </Button>
                <Button onClick={() => setStep(4)} disabled={coverUploading}>
                  下一步
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <Card className="border-gray-100 shadow-xl">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 p-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <CardTitle className="text-lg font-bold">上传完成</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4 shadow-xl">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">书籍上传成功！</h3>
              <p className="text-gray-600">
                您的书籍已创建完成，以下是当前的信息概览。
              </p>
            </div>

            {bookInfo && (
              <div className="grid gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-left">
                <div className="flex gap-4">
                  <div className="w-24 aspect-[2/3] overflow-hidden rounded border bg-white flex items-center justify-center">
                    {bookInfoCoverUrl ? (
                      <img src={bookInfoCoverUrl} alt="封面" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-400">无封面</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <h4 className="text-base font-semibold text-gray-900">{bookInfo.title}</h4>
                    <p className="text-gray-600">作者：{bookInfo.author || '未知'}</p>
                    {bookInfo.isbn && <p className="text-gray-600">ISBN：{bookInfo.isbn}</p>}
                    <p className="text-gray-600">状态：{bookInfo.status === 'published' ? '已发布' : bookInfo.status === 'unlisted' ? '未发布' : '已归档'}</p>
                    {bookInfo.summary && (
                      <p className="text-gray-600">简介：{bookInfo.summary}</p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="font-medium text-gray-800 mb-1">可用格式</p>
                  {bookInfo.availableFormats.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bookInfo.availableFormats.map((format) => (
                        <span key={format} className="inline-flex items-center rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                          {format.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">尚未登记资产</p>
                  )}
                </div>

                <div>
                  <p className="font-medium text-gray-800 mb-1">主题</p>
                  {bookInfo.themes && bookInfo.themes.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bookInfo.themes.map((theme) => (
                        <span
                          key={theme.id}
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
                          style={{
                            backgroundColor: theme.color ? `${theme.color}20` : '#e5e7eb',
                            color: theme.color || '#374151',
                            border: `1px solid ${theme.color || '#d1d5db'}`,
                          }}
                        >
                          {theme.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">未选择主题</p>
                  )}
                </div>

                <div>
                  <p className="font-medium text-gray-800 mb-1">标签</p>
                  {bookInfo.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bookInfo.tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded bg-gray-200 px-2 py-1 text-xs text-gray-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">暂无标签</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={() => navigate('/')}>返回列表</Button>
              <Button onClick={() => navigate(`/books/${bookId}`)}>查看详情</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </div>
  )
}
